import { Trans, Plural } from '@lingui/react/macro';
import { MapPin, Plus, ChevronRight } from 'lucide-react';
import { Link, useNavigate } from '@tanstack/react-router';
import { useSuspenseQuery } from '@tanstack/react-query';
import { Button } from '@/common/components/ui/button';
import { cn } from '@/lib/utils';
import { useSupplyPoint } from '@/features/supply-points/context/useSupplyPoint';
import { supplyPointQueries } from '@/features/supply-points/queries';
import type { SupplyPoint } from '@/features/supply-points/services/schemas';
import type { Organization } from '@/features/organizations/services/schemas';

export function SupplyPointsTab({ org }: { org: Organization }) {
  const navigate = useNavigate();
  const { setActiveSupplyPointId } = useSupplyPoint();
  const { data: supplyPoints } = useSuspenseQuery(supplyPointQueries.byOrganization(org.id));

  const handleSupplyPointClick = (sp: SupplyPoint) => {
    setActiveSupplyPointId(sp.id);
    void navigate({ to: '/' });
  };

  if (supplyPoints.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-center">
        <div className="flex size-16 items-center justify-center rounded-2xl bg-muted mb-4">
          <MapPin size={28} className="text-muted-foreground/40" />
        </div>
        <p className="text-muted-foreground">
          <Trans>This organization has no supply points yet.</Trans>
        </p>
        <p className="text-sm text-muted-foreground/70 mt-1 mb-5">
          <Trans>Add a supply point and assign it to this organization.</Trans>
        </p>
        <Button asChild className="gap-1.5">
          <Link to="/supply-points/new" search={{ orgId: org.id }}>
            <Plus size={14} />
            <Trans>Add supply point</Trans>
          </Link>
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">
          <Plural value={supplyPoints.length} one="# supply point" other="# supply points" />
        </p>
        <Button asChild className="gap-1.5">
          <Link to="/supply-points/new" search={{ orgId: org.id }}>
            <Plus size={14} />
            <Trans>Add supply point</Trans>
          </Link>
        </Button>
      </div>
      <div className="rounded-2xl border border-border overflow-hidden bg-card">
        <div className="hidden sm:grid grid-cols-[1fr_1fr_40px] gap-4 px-5 py-3 bg-muted/50 border-b border-border text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
          <span>
            <Trans>Name</Trans>
          </span>
          <span>
            <Trans>Address</Trans>
          </span>
          <span />
        </div>
        {supplyPoints.map((sp, idx) => (
          <button
            key={sp.id}
            onClick={() => handleSupplyPointClick(sp)}
            className={cn(
              'w-full text-left grid grid-cols-1 sm:grid-cols-[1fr_1fr_40px] gap-2 sm:gap-4 px-5 py-4 transition-colors group hover:bg-accent/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
              idx < supplyPoints.length - 1 ? 'border-b border-border' : ''
            )}
          >
            <div className="flex items-center gap-3">
              <div className="flex size-8 items-center justify-center rounded-lg bg-primary/10 shrink-0">
                <MapPin size={14} className="text-primary" />
              </div>
              <span className="font-medium text-foreground text-sm">{sp.name}</span>
            </div>
            <div className="text-sm text-muted-foreground hidden sm:flex items-center">
              {sp.street}, {sp.city}
            </div>
            <div className="flex items-center justify-center">
              <ChevronRight
                size={16}
                className="text-muted-foreground/40 group-hover:text-muted-foreground transition-colors"
              />
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}
